import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
  Divider,
  LinearProgress,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  Chat as ChatIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  PersonAdd as PersonAddIcon,
  Groups as GroupsIcon,
  Business as BusinessIcon,
  Share as ShareIcon,
  Star as StarIcon,
  TrendingUp as TrendingUpIcon,
  ArrowForward as ArrowForwardIcon,
  Forum as ForumIcon,
  ConnectWithoutContact as ConnectIcon,
  QuestionAnswer as QuestionAnswerIcon,
  MoreHoriz as MoreHorizIcon
} from '@mui/icons-material';

// Empty State Component for 0 conversations but with network contacts
const EmptyWithNetworkState = ({ potentialContacts, onUserClick, selectedUser, onFindPeople }) => {
  const navigate = useNavigate();

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Messages</Typography>
        <MoreHorizIcon />
      </Box>

      {/* Search Bar */}
      <TextField
        label="Search"
        variant="outlined"
        fullWidth
        sx={{ mb: 3, backgroundColor: '#f5f5f5' }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <FilterListIcon style={{ cursor: 'pointer' }} />
            </InputAdornment>
          ),
        }}
      />

      {/* Encouragement Card */}
      <Paper variant='outlined' sx={{ p: 3, mb: 3, border: 'none', textAlign: 'center' }}>
        <ForumIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2, opacity: 0.8 }} />
        <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
          Start Messaging Your Network
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          You have {potentialContacts.length} people in your network. Click on anyone below to start a conversation!
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<PersonAddIcon />}
          onClick={onFindPeople}
        >
          Find More People
        </Button>
      </Paper>

      {/* Network Contacts List */}
      <Paper variant='outlined' sx={{ p: 2, mb: 3, border: 'none' }}>
        <Typography variant="subtitle1" gutterBottom sx={{ px: 1 }}>
          Your Network ({potentialContacts.length})
        </Typography>
        
        <Box sx={{ 
          maxHeight: 'calc(100vh - 400px)',
          overflowY: 'auto',
          msOverflowStyle: 'none', 
          scrollbarWidth: 'thin',
          scrollbarColor: 'transparent transparent',
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': { background: 'transparent' },
        }}>
          {potentialContacts.slice(0, 20).map((contact, index) => (
            <React.Fragment key={contact.otherUserId}>
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  pl: 1,
                  py: 1.5,
                  borderLeft: selectedUser === contact.otherUserId ? '5px solid blue' : 'none',
                  cursor: 'pointer',
                  borderRadius: 1,
                  transition: 'all 0.2s',
                  '&:hover': { 
                    bgcolor: 'grey.100',
                    transform: 'translateX(2px)'
                  }
                }}
                onClick={() => onUserClick(contact)}
              >
                <Avatar src={contact.photoUrl} sx={{ mr: 2, width: 40, height: 40 }}>
                  {contact.displayName?.charAt(0)}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="subtitle2">{contact.displayName}</Typography>
                    <Chip 
                      label="New" 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                      sx={{ fontSize: '0.7rem', height: '20px' }}
                    />
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontStyle: 'italic' }}
                  >
                    Start a conversation...
                  </Typography>
                </Box>
                <ArrowForwardIcon color="action" sx={{ opacity: 0.5 }} />
              </Box>
              {index < potentialContacts.slice(0, 20).length - 1 && (
                <Divider sx={{ my: 1 }} />
              )}
            </React.Fragment>
          ))}
          
          {potentialContacts.length > 20 && (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="body2" color="text.secondary">
                +{potentialContacts.length - 20} more contacts
              </Typography>
              <Button 
                variant="text" 
                size="small" 
                onClick={onFindPeople}
                sx={{ mt: 1 }}
              >
                View All
              </Button>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Quick tip */}
      <Paper variant='outlined' sx={{ p: 2, border: 'none', bgcolor: 'primary.50' }}>
        <Typography variant="body2" color="primary.main" sx={{ textAlign: 'center' }}>
          💡 Tip: Click on any name above to send your first message and start building stronger relationships!
        </Typography>
      </Paper>
    </Box>
  );
};

// Empty State Component for 0 conversations and no network
const EmptyNoNetworkState = ({ onStartConversation, onFindPeople }) => {
  const navigate = useNavigate();

  const benefits = [
    {
      icon: <StarIcon color="primary" />,
      title: "Build Relationships",
      description: "Direct messaging helps build stronger business relationships"
    },
    {
      icon: <TrendingUpIcon color="primary" />,
      title: "Quick Communication", 
      description: "Get instant responses for time-sensitive business matters"
    },
    {
      icon: <BusinessIcon color="primary" />,
      title: "Collaborate Better",
      description: "Discuss projects and opportunities in private conversations"
    },
    {
      icon: <ConnectIcon color="primary" />,
      title: "Network Growth",
      description: "Personal conversations lead to stronger professional networks"
    }
  ];

  const quickActions = [
    {
      icon: <PersonAddIcon />,
      title: "Find People to Message",
      description: "Search for colleagues and business partners",
      action: () => onFindPeople()
    },
    {
      icon: <GroupsIcon />,
      title: "Browse Your Network",
      description: "Message people you're already connected with",
      action: () => navigate('/network')
    },
    {
      icon: <ShareIcon />,
      title: "Share Your Profile",
      description: "Let others know they can message you",
      action: () => navigate('/profile/share')
    }
  ];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Messages</Typography>
        <MoreHorizIcon />
      </Box>

      {/* Search Bar */}
      <TextField
        label="Search"
        variant="outlined"
        fullWidth
        sx={{ mb: 3, backgroundColor: '#f5f5f5' }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <FilterListIcon style={{ cursor: 'pointer' }} />
            </InputAdornment>
          ),
        }}
      />

      {/* Main Empty State Card */}
      <Paper variant='outlined' sx={{ p: 4, mb: 3, border: 'none', textAlign: 'center', minHeight: '200px' }}>
        <Box sx={{ mb: 3 }}>
          <ForumIcon sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom color="primary" fontWeight="bold">
            Start Your First Conversation
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 400, mx: 'auto' }}>
            Connect and communicate directly with your business network. 
            Build stronger relationships through meaningful conversations.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<PersonAddIcon />}
            onClick={onFindPeople}
            sx={{ minWidth: 180 }}
          >
            Find People
          </Button>
          <Button
            variant="outlined"
            size="large"
            startIcon={<GroupsIcon />}
            onClick={() => navigate('/network')}
          >
            Browse Network
          </Button>
        </Box>
      </Paper>

      {/* Benefits Grid */}
      <Paper variant='outlined' sx={{ p: 3, mb: 3, border: 'none' }}>
        <Typography variant="h6" gutterBottom sx={{ textAlign: 'center', mb: 3 }}>
          Why Use Direct Messaging?
        </Typography>
        <Grid container spacing={2}>
          {benefits.map((benefit, index) => (
            <Grid item xs={12} sm={6} key={index}>
              <Card variant="outlined" sx={{ p: 2, height: '100%', border: 'none', bgcolor: 'grey.50' }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <Box sx={{ flexShrink: 0 }}>
                    {benefit.icon}
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      {benefit.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {benefit.description}
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Quick Actions */}
      <Paper variant='outlined' sx={{ p: 3, border: 'none' }}>
        <Typography variant="h6" gutterBottom>
          Get Started Today
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {quickActions.map((action, index) => (
            <Card 
              key={index}
              variant="outlined" 
              sx={{ 
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': { 
                  bgcolor: 'grey.50',
                  transform: 'translateY(-1px)',
                  boxShadow: 1
                }
              }}
              onClick={action.action}
            >
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  {action.icon}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1">
                    {action.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {action.description}
                  </Typography>
                </Box>
                <ArrowForwardIcon color="action" />
              </CardContent>
            </Card>
          ))}
        </Box>
      </Paper>
    </Box>
  );
};
  

// Growing Chat State for 1-2 conversations
const GrowingChatState = ({ conversations, onStartConversation, onFindPeople, onUserClick, selectedUser }) => {
  const navigate = useNavigate();
  
  const progress = Math.min((conversations.length / 5) * 100, 100); // Progress towards 5 conversations
  
  const nextSteps = [
    {
      icon: <PersonAddIcon />,
      title: "Find More People",
      description: `Add ${Math.max(5 - conversations.filter(c => !c.isNetworkContact).length, 0)} more conversations to build your network`,
      action: () => onFindPeople()
    },
    {
      icon: <QuestionAnswerIcon />,
      title: "Start New Conversations",
      description: "Reach out to your existing connections",
      action: () => navigate('/network')
    },
    {
      icon: <ShareIcon />,
      title: "Invite Others",
      description: "Share your profile to receive more messages",
      action: () => navigate('/profile/share')
    }
  ];

  const actualConversations = conversations.filter(c => !c.isNetworkContact);

  return (
    <Box sx={{ minHeight: '500px' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Messages</Typography>
        <MoreHorizIcon />
      </Box>

      {/* Search Bar */}
      <TextField
        label="Search"
        variant="outlined"
        fullWidth
        sx={{ mb: 3, backgroundColor: '#f5f5f5' }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <FilterListIcon style={{ cursor: 'pointer' }} />
            </InputAdornment>
          ),
        }}
      />

      {/* Progress Card */}
      <Paper variant='outlined' sx={{ p: 3, mb: 3, border: 'none' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <ForumIcon color="primary" />
          <Typography variant="h6">
            Growing Your Conversations
          </Typography>
          <Chip 
            label={`${actualConversations.length}/5`} 
            color="primary" 
            variant="outlined" 
            size="small" 
          />
        </Box>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Great start! You have {actualConversations.length} active conversation{actualConversations.length !== 1 ? 's' : ''}. 
          Keep building relationships through meaningful conversations.
        </Typography>
        
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2">Chat Progress</Typography>
            <Typography variant="body2" color="primary">
              {Math.round(progress)}%
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={progress} 
            sx={{ height: 8, borderRadius: 1 }}
          />
        </Box>

        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={onFindPeople}
          fullWidth
        >
          Find More People
        </Button>
      </Paper>

      {/* Current Conversations */}
      <Paper variant='outlined' sx={{ p: 3, mb: 3, border: 'none' }}>
        <Typography variant="h6" gutterBottom>
          Your Conversations ({conversations.length})
        </Typography>
        
        {/* Render the actual chat list */}
        <Box sx={{ 
          maxHeight: 'calc(100vh - 600px)',
          overflowY: 'auto',
          msOverflowStyle: 'none', 
          scrollbarWidth: 'thin',
          scrollbarColor: 'transparent transparent',
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': { background: 'transparent' },
        }}>
          {conversations.map((user, index) => (
            <React.Fragment key={user.otherUserId}>
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  pl: 1,
                  py: 1,
                  borderLeft: selectedUser === user.otherUserId ? '5px solid blue' : 'none',
                  cursor: 'pointer',
                  borderRadius: 1,
                  transition: 'all 0.2s',
                  '&:hover': { 
                    bgcolor: 'grey.50',
                    transform: user.isNetworkContact ? 'translateX(2px)' : 'none'
                  }
                }}
                onClick={() => onUserClick(user)}
              >
                <Avatar src={user.photoUrl} sx={{ mr: 2 }}>
                  {user.displayName?.charAt(0)}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="subtitle2">{user.displayName}</Typography>
                    {user.isNetworkContact ? (
                      <Chip 
                        label="New" 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                        sx={{ fontSize: '0.7rem', height: '20px' }}
                      />
                    ) : (
                      <Typography variant="caption">{user.formattedMsgTime}</Typography>
                    )}
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontStyle: user.isNetworkContact ? 'italic' : 'normal'
                    }}
                  >
                    {user.isNetworkContact 
                      ? "Start a conversation..." 
                      : user.lastMessage?.split(' ').slice(0, 5).join(' ')
                    }
                  </Typography>
                </Box>
                {user.isNetworkContact && (
                  <ArrowForwardIcon color="action" sx={{ opacity: 0.5 }} />
                )}
              </Box>
              {index < conversations.length - 1 && (
                <Divider sx={{ my: 1 }} />
              )}
            </React.Fragment>
          ))}
        </Box>
      </Paper>

      {/* Next Steps */}
      <Paper variant='outlined' sx={{ p: 3, border: 'none' }}>
        <Typography variant="h6" gutterBottom>
          Keep Growing
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {nextSteps.map((step, index) => (
            <Card 
              key={index}
              variant="outlined" 
              sx={{ 
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': { 
                  bgcolor: 'grey.50',
                  transform: 'translateY(-1px)',
                  boxShadow: 1
                }
              }}
              onClick={step.action}
            >
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36 }}>
                  {step.icon}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2">
                    {step.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {step.description}
                  </Typography>
                </Box>
                <ArrowForwardIcon color="action" />
              </CardContent>
            </Card>
          ))}
        </Box>
      </Paper>
    </Box>
  );
};

// Normal Chat State with search and full list
const NormalChatState = ({ conversations, onUserClick, selectedUser }) => {
  return (
    <Box sx={{ minHeight: '500px' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Messages</Typography>
        <MoreHorizIcon />
      </Box>

      {/* Search Bar */}
      <TextField
        label="Search"
        variant="outlined"
        fullWidth
        sx={{ mb: 3, backgroundColor: '#f5f5f5' }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <FilterListIcon style={{ cursor: 'pointer' }} />
            </InputAdornment>
          ),
        }}
      />

      {/* Chat List */}
      <Box sx={{ 
        maxHeight: 'calc(100vh - 200px)',
        overflowY: 'auto',
        msOverflowStyle: 'none', 
        scrollbarWidth: 'thin',
        scrollbarColor: 'transparent transparent',
        '&::-webkit-scrollbar': { width: '4px' },
        '&::-webkit-scrollbar-track': { background: 'transparent' },
        '&::-webkit-scrollbar-thumb': { background: 'transparent' },
      }}>
        {conversations.map((user, index) => (
          <React.Fragment key={user.otherUserId}>
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center',
                pl: 1,
                py: 1,
                borderLeft: selectedUser === user.otherUserId ? '5px solid blue' : 'none',
                cursor: 'pointer',
                borderRadius: 1,
                transition: 'all 0.2s',
                '&:hover': { 
                  bgcolor: 'grey.50',
                  transform: user.isNetworkContact ? 'translateX(2px)' : 'none'
                }
              }}
              onClick={() => onUserClick(user)}
            >
              <Avatar src={user.photoUrl} sx={{ mr: 2 }}>
                {user.displayName?.charAt(0)}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="subtitle2">{user.displayName}</Typography>
                  {user.isNetworkContact ? (
                    <Chip 
                      label="New" 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                      sx={{ fontSize: '0.7rem', height: '20px' }}
                    />
                  ) : (
                    <Typography variant="caption">{user.formattedMsgTime}</Typography>
                  )}
                </Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontStyle: user.isNetworkContact ? 'italic' : 'normal'
                  }}
                >
                  {user.isNetworkContact 
                    ? "Start a conversation..." 
                    : user.lastMessage?.split(' ').slice(0, 5).join(' ')
                  }
                </Typography>
              </Box>
              {user.isNetworkContact && (
                <ArrowForwardIcon color="action" sx={{ opacity: 0.5 }} />
              )}
            </Box>
            {index < conversations.length - 1 && (
              <Divider sx={{ my: 1 }} />
            )}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
};

// Main component to determine which state to show
const ChatMiddleColumn = ({ 
  conversations = [], 
  potentialContacts = [],
  onStartConversation,
  onFindPeople,
  onUserClick,
  selectedUser,
  otherUser // For when navigating from another page
}) => {
  // Add potential contacts that aren't already in conversations
  const existingUserIds = conversations.map(c => c.otherUserId);
  const newPotentialContacts = potentialContacts.filter(
    contact => !existingUserIds.includes(contact.otherUserId)
  );

  // Determine which state to show based on conversation count and network contacts
  if (conversations.length === 0 && potentialContacts.length > 0) {
    // No conversations but have network contacts
    return (
      <EmptyWithNetworkState 
        potentialContacts={potentialContacts}
        onUserClick={onUserClick}
        selectedUser={selectedUser}
        onFindPeople={onFindPeople}
      />
    );
  } else if (conversations.length === 0 && potentialContacts.length === 0) {
    // No conversations and no network contacts
    return (
      <EmptyNoNetworkState 
        onStartConversation={onStartConversation}
        onFindPeople={onFindPeople}
      />
    );
  } else if (conversations.length > 0 && conversations.length <= 2) {
    // Few conversations - show growing state with potential contacts mixed in
    const combinedList = [...conversations, ...newPotentialContacts.slice(0, 3)];
    return (
      <GrowingChatState 
        conversations={combinedList}
        onStartConversation={onStartConversation}
        onFindPeople={onFindPeople}
        onUserClick={onUserClick}
        selectedUser={selectedUser}
      />
    );
  } else {
    // Many conversations - show normal state with some potential contacts
    const combinedList = [...conversations, ...newPotentialContacts.slice(0, 5)];
    return (
      <NormalChatState
        conversations={combinedList}
        onUserClick={onUserClick}
        selectedUser={selectedUser}
      />
    );
  }
};

export default ChatMiddleColumn;